import React from "react";
import _ from "lodash";
import moment from "moment-timezone";
import numeral from "numeral";
import LoadingUI from "components/common/LoadingUI";
import ModalWrapper from "components/common/ModalWrapper";
import HighchartWrapper from "components/common/HighchartWrapper";
import { DATE_RANGE_TYPE } from "components/playground/home-dashboard-v2/DateHelper";
import DateRangePicker from "components/common/DateRangePicker";
import SwitchTabs from "components/common/SwitchTabs";

import { NET_REV, netRevTitle } from "constants/Unit";
import { AnatomyAPI, NetworkAPI } from "apis";
import { addChartBgSection, getCurrencySymbol } from "helpers/Utils";
import { normalizeRevenue } from "helpers/Chart";
import { Z_INDEX } from "constants/UI";

class UnitRevenueLiftTrendModal extends React.Component {
  constructor(props) {
    super(props);

    const today = moment().format("YYYY-MM-DD");

    this.state = {
      errmsg: "",
      isLoading: false,
      startDate: moment(today).subtract(30, "d").format("YYYY-MM-DD"),
      endDate: today,
      dataType: this.props.dataType,

      originData: null,
      options: null,

      currency: "",
      twdConvertRate: 1,
      selectedCurrency: "",
    };

    this.handleTabChange = this.handleTabChange.bind(this);
    this.handleDateRangeFilterChanged =
      this.handleDateRangeFilterChanged.bind(this);
    this.handleDataTypeChange = this.handleDataTypeChange.bind(this);
    this.handleChartOption = this.handleChartOption.bind(this);
    this.handleGetData = this.handleGetData.bind(this);
  }

  async componentDidMount() {
    const { networkId, isInTwd } = this.props;
    try {
      this.setState({ isLoading: true });
      const { currency, twdConvertRate } =
        await NetworkAPI.getLatestTwdConvertRate({ networkId });
      if (_.isNil(currency) || _.isNil(twdConvertRate)) {
        throw new Error("Failed to get network currency info");
      }
      this.setState(
        {
          currency,
          twdConvertRate,
          selectedCurrency: isInTwd ? "TWD" : currency,
        },
        () => {
          this.handleGetData();
        },
      );
    } catch (e) {
      this.setState({ errmsg: e.message, isLoading: false });
    }
  }

  handleTabChange(tab) {
    this.setState({ selectedCurrency: tab }, () => {
      this.handleChartOption();
    });
  }

  handleDateRangeFilterChanged({ startDate, endDate }) {
    this.setState(
      {
        startDate: moment(startDate).format("YYYY-MM-DD"),
        endDate: moment(endDate).format("YYYY-MM-DD"),
        originData: null,
      },
      () => {
        this.handleGetData();
      },
    );
  }

  handleDataTypeChange(dataType) {
    this.setState({ dataType }, () => {
      this.handleGetData();
    });
  }

  async handleGetData() {
    if (this.state.originData && this.state.originData[this.state.dataType]) {
      this.handleChartOption();
      return;
    }

    this.setState({ isLoading: true, options: null, errmsg: "" });
    try {
      const result = await AnatomyAPI.getUnitRevLiftTrend({
        extGamUnitId: this.props.extUnitId,
        startDate: this.state.startDate,
        endDate: this.state.endDate,
        reportType: this.state.dataType,
      });

      this.setState(
        {
          originData: {
            ...(this.state.originData || {}),
            [this.state.dataType]: result,
          },
        },
        () => {
          this.handleChartOption();
        },
      );
    } catch (e) {
      this.setState({ errmsg: e.message });
    }

    this.setState({ isLoading: false });
  }

  handleChartOption() {
    const { dataType, originData, currency, selectedCurrency, twdConvertRate } =
      this.state;

    const data = _.cloneDeep(originData[dataType]);
    if (!data) return;

    const dataSeries = _(data)
      .map((d) => {
        const {
          incr_rev = 0,
          cost = 0,
          base_rev = 0,
          twd_convert_rate = twdConvertRate,
        } = d;
        const increased = incr_rev - cost;
        const lift = base_rev > 0 ? increased / base_rev : 0;
        const y = _.round(lift * 100, 2);
        const revenue =
          selectedCurrency === "TWD" ? increased * twd_convert_rate : increased;

        return {
          x: new Date(d.date).getTime(),
          y,
          color: y < 0 && "red",
          increased: normalizeRevenue(revenue),
        };
      })
      .sortBy("x")
      .value();

    const dataMin = _.get(_.minBy(dataSeries, "y"), "y", 0);

    const currencySymbol = getCurrencySymbol(
      selectedCurrency === "TWD" ? "TWD" : currency,
    );
    const options = {
      chart: {
        height: 320,
        zoomType: "x",
        events: {
          load: function () {
            addChartBgSection(this);
          },
        },
      },
      title: {
        text: "",
      },
      tooltip: {
        useHTML: true,
        formatter: function () {
          return `<b>${moment(this.x).format("YYYY-MM-DD ddd")}</b><br/><b>${
            this.y
          }%</b><br/>${currencySymbol}${numeral(this.point.increased).format(
            "0,0.00",
          )}<br/>`;
        },
      },
      yAxis: {
        title: {
          text: "%",
        },
        min: dataMin < 0 ? dataMin : 0,
        plotBands: this._createYAxisPlotBands(),
      },

      series: [
        {
          name: netRevTitle.get(dataType),
          type: "line",
          data: dataSeries,
        },
      ],
    };

    this.setState({ options });
  }

  _createYAxisPlotBands() {
    return [
      {
        from: -9999999,
        to: 0,
        color: "#f4e6e6",
      },
    ];
  }

  render() {
    const { unitId, unitName, networkName, networkId } = this.props;

    const {
      startDate,
      endDate,
      dataType,
      options,
      isLoading,
      errmsg,
      currency,
      selectedCurrency,
    } = this.state;

    return (
      <ModalWrapper
        isOpen={true}
        handleClose={this.props.handleClose}
        showCloseFooter={true}
        customOverlayStyle={{ zIndex: Z_INDEX.MODAL_INDEX }}
        title={{
          label: "Revenue Lift Charts ",
          highlight: `${unitId} - ${unitName}`,
        }}
      >
        {networkName && networkId && (
          <>
            <div className="mb-1 font-semibold text-gray-600">Network:</div>
            <div className="mb-4 flex">
              <div className="mr-1 font-semibold text-gray-800">
                {networkId}
              </div>
              <div className="truncate mr-1 text-gray-800" title={networkName}>
                {networkName}
              </div>
            </div>
          </>
        )}
        {/* <div className="mb-1 font-semibold text-gray-600">Unit:</div>
        <div className="mb-4 flex">
          <div className="mr-1 font-semibold text-gray-800">{unitId}</div>
          <div className="truncate mr-1 text-gray-800" title={unitName}>
            {unitName}
          </div>
        </div> */}
        <div className="mb-1 font-semibold text-gray-600">Date Range:</div>
        <div className="mb-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            format="YYYY-MM-DD ddd"
            handleDateRangeChanged={this.handleDateRangeFilterChanged}
            dateRanges={[
              DATE_RANGE_TYPE.THIS_WEEK,
              DATE_RANGE_TYPE.LAST_WEEK,
              DATE_RANGE_TYPE.LAST_2_WEEKS,
              DATE_RANGE_TYPE.THIS_MONTH,
              DATE_RANGE_TYPE.LAST_MONTH,
              DATE_RANGE_TYPE.LAST_MONTH_TO_TODAY,
            ]}
          ></DateRangePicker>
        </div>
        <div className="mb-1 font-semibold text-gray-600">Data Type:</div>
        <div className="border-b mb-2 flex gap-2 pb-2">
          {_.map(NET_REV, (value) => {
            return (
              <button
                className={`rounded mr-1 px-4 py-1 font-semibold text-gray-700 ${
                  dataType === value
                    ? "bg-blue-200"
                    : "bg-gray-100 hover:bg-gray-200 hover:text-gray-800"
                }`}
                key={value}
                onClick={() => this.handleDataTypeChange(value)}
              >
                {netRevTitle.get(value)}
              </button>
            );
          })}
        </div>
        <div className="w-full">
          {isLoading && <LoadingUI></LoadingUI>}
          {errmsg !== "" && <div className="text-red-800">{errmsg}</div>}
          {!isLoading && currency !== "" && (
            <div className="flex justify-end">
              <SwitchTabs
                tabs={_.uniq([currency, "TWD"]).map((cur) => ({
                  label: cur,
                  value: cur,
                }))}
                selectedTabKey={selectedCurrency}
                handleChange={(tab) => this.handleTabChange(tab)}
              ></SwitchTabs>
            </div>
          )}
          {options && (
            <HighchartWrapper
              options={options}
              key={`${dataType}-${selectedCurrency}`}
            ></HighchartWrapper>
          )}
        </div>
      </ModalWrapper>
    );
  }
}

export default UnitRevenueLiftTrendModal;
